import { useEffect, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { usePrivy } from '@privy-io/react-auth'
import { useCreateWallet } from '@privy-io/react-auth/solana'
import { useCreateWallet as useCreateExtendedWallet } from '@privy-io/react-auth/extended-chains'
import { useStore } from '../store'
import { registerUnauthorizedHandler } from '../services/api'
import { validateVenue } from '../services/venue'
import { logSessionStarted } from '../services/analytics'

export function AuthSync() {
  const { user, ready, authenticated, getAccessToken, logout, createWallet: createEthWallet } = usePrivy()
  const { createWallet: createSolWallet } = useCreateWallet()
  const { createWallet: createStellarWallet } = useCreateExtendedWallet()
  const { 
    app: { venue, pendingVenueId }, 
    setVenue, 
    setAccessToken, 
    setPendingVenueId,
    setVenueError
  } = useStore()
  const [searchParams] = useSearchParams()
  const failedAttempts = useRef<Set<string>>(new Set()) // Track failed IDs to prevent loops
  const lastDispatchedVenueId = useRef<string | null>(null) // Track last dispatched to prevent re-validation loops
  const isValidating = useRef(false) // Prevent concurrent validations
  const walletCreationInProgress = useRef(false) // Prevent multiple wallet creation attempts

  // Sync Privy User ID & Access Token
  useEffect(() => {
    if (ready) {
      if (user) {
         getAccessToken().then((token) => {
            setAccessToken(token)
         })
      } else {
        setAccessToken(null)
      }
    }
  }, [user, ready, getAccessToken, setAccessToken])

  // Create embedded wallets if user doesn't have them
  useEffect(() => {
    if (!ready || !authenticated || !user) return
    if (walletCreationInProgress.current) return

    const hasEthWallet = user.linkedAccounts?.some(
      (account) =>
        account.type === 'wallet' &&
        account.walletClientType === 'privy' &&
        account.chainType === 'ethereum'
    )

    const hasSolWallet = user.linkedAccounts?.some(
      (account) =>
        account.type === 'wallet' &&
        account.walletClientType === 'privy' &&
        account.chainType === 'solana'
    )

    const hasStellarWallet = user.linkedAccounts?.some(
      (account) =>
        account.type === 'wallet' &&
        account.walletClientType === 'privy' &&
        account.chainType === 'stellar'
    )

    if (hasEthWallet && hasSolWallet && hasStellarWallet) return

    const createWallets = async () => {
      walletCreationInProgress.current = true
      try {
        if (!hasEthWallet) {
          await createEthWallet()
        }
        if (!hasSolWallet) {
          await createSolWallet()
        }
        if (!hasStellarWallet) {
          await createStellarWallet({ chainType: 'stellar' })
        }
      } catch (err) {
        console.error('Failed to create embedded wallets:', err)
      } finally {
        walletCreationInProgress.current = false
      }
    }

    createWallets()
  }, [ready, authenticated, user, createEthWallet, createSolWallet, createStellarWallet])

  // Register API 401 handler
  useEffect(() => {
    registerUnauthorizedHandler(async () => {
      try {
        if (!ready) return null
        
        // Attempt to refresh token via Privy
        const newToken = await getAccessToken()
        if (newToken) {
          setAccessToken(newToken)
          return newToken
        }
      } catch (err) {
        console.error('Failed to refresh token:', err)
      }
      
      // Refresh failed or returned null - logout user
      await logout()
      return null
    })
  }, [ready, getAccessToken, setAccessToken, logout])

  // 1. Capture Venue ID from URL into Pending State
  useEffect(() => {
    const urlVenueId = searchParams.get('venue_id')
    if (urlVenueId &&
        lastDispatchedVenueId.current !== urlVenueId &&
        !failedAttempts.current.has(urlVenueId)
    ) {
      lastDispatchedVenueId.current = urlVenueId
      setPendingVenueId(urlVenueId)
    }
  }, [searchParams, setPendingVenueId])

  // 2. Validate & Activate Pending Venue when Authenticated
  useEffect(() => {
    // Only proceed if authenticated and we have a pending venue
    if (!authenticated || !pendingVenueId) return

    // Always re-validate to get fresh venue configuration
    if (isValidating.current) return
    const validate = async () => {
      isValidating.current = true
      // Clear any previous error before starting
      setVenueError(null)

      try {
        const session = await validateVenue(pendingVenueId)
        setVenue(session)
        logSessionStarted(pendingVenueId)
        setPendingVenueId(null) // Clear pending after success
      } catch (err) {
        console.error('Failed to validate venue:', err)
        failedAttempts.current.add(pendingVenueId) // Mark as failed
        setPendingVenueId(null) // Clear pending on error to prevent loops

        // If we were attempting to switch venues (i.e. we had an active one),
        // clear the old invalid session to prevent "Zombie" sessions at new location.
        if (venue?.id) {
            setVenue(null)
        }

        // Set explicit error to trigger Active Error UI
        setVenueError("INVALID_VENUE")
      } finally {
        isValidating.current = false
      }
    }

    validate()
  }, [authenticated, pendingVenueId, setVenue, setPendingVenueId])

  return null
}
